"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface Request {
  resolve: () => void;
  cleanup: () => void;
  reject: (error: unknown) => void;
}

/** Wait for teardown AND RainbowKit's disconnected render, without timers. */
export function useEvmPickerChange({
  disconnected,
  open,
  disconnect,
  reset,
}: {
  disconnected: boolean;
  open: (() => void) | undefined;
  disconnect: () => Promise<unknown>;
  reset: () => void;
}) {
  const request = useRef<Request | null>(null);
  const [ready, setReady] = useState<Request | null>(null);
  const cancel = useCallback(() => {
    const pending = request.current;
    request.current = null;
    setReady(null);
    pending?.cleanup();
    pending?.reject(new Error("Wallet change cancelled."));
  }, []);
  useEffect(
    () => () => {
      const pending = request.current;
      request.current = null;
      pending?.cleanup();
      pending?.reject(new Error("Wallet change cancelled."));
    },
    []
  );
  useEffect(() => {
    if (!ready || request.current !== ready || !disconnected) return;
    request.current = null;
    setReady(null);
    ready.cleanup();
    try {
      if (!open)
        throw new Error(
          "The EVM wallet picker is unavailable. Reload and try again."
        );
      open();
      ready.resolve();
    } catch (error) {
      ready.reject(error);
    }
  }, [disconnected, open, ready]);
  const change = useCallback(
    (signal?: AbortSignal) => {
      if (signal?.aborted)
        return Promise.reject(new Error("Wallet change cancelled."));
      if (request.current)
        return Promise.reject(new Error("Wallet change in progress."));
      reset();
      return new Promise<void>((resolve, reject) => {
        const abort = () => {
          if (request.current !== pending) return;
          request.current = null;
          setReady(null);
          pending.cleanup();
          reject(new Error("Wallet change cancelled."));
        };
        const pending = {
          resolve,
          reject,
          cleanup: () => signal?.removeEventListener("abort", abort),
        };
        request.current = pending;
        signal?.addEventListener("abort", abort, { once: true });
        void disconnect().then(
          () => {
            if (request.current === pending) setReady(pending);
          },
          (error: unknown) => {
            if (request.current !== pending) return;
            request.current = null;
            pending.cleanup();
            reject(error);
          }
        );
      });
    },
    [disconnect, reset]
  );
  return { change, cancel };
}
